import { useEffect, useState, useCallback } from 'react'
import { Wifi, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, Cpu } from 'lucide-react'
import { platformAnalyticsService } from '../../../services/platformAnalyticsService'
import type { DeviceHealthResponse, DeviceWithHealth, HealthStatus } from '../../../types/api.types'

function healthColor(status: HealthStatus): string {
  const map: Record<HealthStatus, string> = {
    HEALTHY:  'bg-green-100 text-green-800',
    DEGRADED: 'bg-amber-100 text-amber-800',
    OFFLINE:  'bg-red-100 text-red-800',
    CRITICAL: 'bg-red-200 text-red-900',
    BLOCKED:  'bg-gray-100 text-gray-600',
    UNKNOWN:  'bg-slate-100 text-slate-500',
  }
  return map[status] ?? 'bg-slate-100 text-slate-400'
}

function healthDot(status: HealthStatus): string {
  const map: Record<HealthStatus, string> = {
    HEALTHY:  'bg-green-500',
    DEGRADED: 'bg-amber-400',
    OFFLINE:  'bg-red-400',
    CRITICAL: 'bg-red-700',
    BLOCKED:  'bg-gray-400',
    UNKNOWN:  'bg-slate-300',
  }
  return map[status] ?? 'bg-slate-300'
}

function fmtRelative(ts: string | null): string {
  if (!ts) return '—'
  const diff = Date.now() - new Date(ts).getTime()
  const h = Math.floor(diff / 3_600_000)
  const m = Math.floor((diff % 3_600_000) / 60_000)
  if (h > 24) return `${Math.floor(h / 24)}d ago`
  if (h > 0) return `${h}h ${m}m ago`
  return `${m}m ago`
}

function OrgHealthRow({ org }: { org: DeviceHealthResponse['organizations'][0] }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [devices, setDevices] = useState<DeviceWithHealth[]>([])
  const [loading, setLoading] = useState(false)
  const pctOnline = org.total_devices ? Math.round((org.online_count / org.total_devices) * 100) : 0

  async function loadDevices() {
    if (open) { setOpen(false); return }
    setOpen(true)
    if (devices.length) return
    setLoading(true)
    try {
      const list = await platformAnalyticsService.getOrgDeviceHealth(org.organization_id)
      setDevices(Array.isArray(list) ? list : [])
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }

  return (
    <>
      <tr
        className="cursor-pointer border-t border-gray-100 hover:bg-gray-50/60"
        onClick={loadDevices}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            <span className="text-[13px] font-medium text-gray-900">{org.organization_name}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-center text-[13px] font-semibold text-gray-900">{org.total_devices}</td>
        <td className="px-4 py-3 text-center">
          <span className="text-[13px] font-semibold text-green-600">{org.online_count}</span>
          <span className="ml-1 text-[11px] text-gray-400">({pctOnline}%)</span>
        </td>
        <td className="px-4 py-3 text-center text-[13px] text-amber-600">{org.degraded_count}</td>
        <td className="px-4 py-3 text-center text-[13px] text-red-500">{org.offline_count}</td>
        <td className="px-4 py-3 text-center text-[13px] text-gray-500">{org.blocked_count}</td>
        <td className="px-4 py-3 text-center text-[13px] text-slate-400">{org.unknown_count}</td>
        <td className="px-4 py-3">
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${pctOnline}%` }} />
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="bg-gray-50/40 px-4 py-3">
            {loading ? (
              <p className="py-4 text-center text-[12px] text-gray-400">Loading devices...</p>
            ) : devices.length === 0 ? (
              <p className="py-4 text-center text-[12px] text-gray-400">No devices found.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="w-full min-w-[640px]">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Device</th>
                      <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Property</th>
                      <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Team</th>
                      <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Health</th>
                      <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Firmware</th>
                      <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Last Seen</th>
                      <th className="px-3 py-2 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Incidents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.device_id} className="border-t border-gray-100 hover:bg-gray-50/40">
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <span className={`h-2 w-2 rounded-full ${healthDot(d.health_status)}`} />
                            <code className="text-[11px] text-gray-600">{d.mac_address}</code>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-[12px] text-gray-700">{d.property_name}</td>
                        <td className="px-3 py-2.5 text-[12px] text-gray-500">{d.team_name ?? '—'}</td>
                        <td className="px-3 py-2.5">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${healthColor(d.health_status)}`}>
                            {d.health_status}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-[11px] text-gray-500">{d.firmware_version ?? '—'}</td>
                        <td className="px-3 py-2.5 text-[12px] text-gray-500">{fmtRelative(d.last_seen_at)}</td>
                        <td className="px-3 py-2.5 text-center">
                          {d.open_incidents > 0 ? (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">{d.open_incidents}</span>
                          ) : (
                            <span className="text-[11px] text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export function PlatformDeviceHealthPage(): JSX.Element {
  const [health, setHealth] = useState<DeviceHealthResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try { setHealth(await platformAnalyticsService.getDeviceHealth()) }
    catch { setHealth(null) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const attentionCount = health ? health.offline_count + health.blocked_count : 0

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Device Health</h1>
          <p className="mt-1 text-sm text-gray-500">Real-time hardware status across all organisations</p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {[
          { label: 'Total', value: health?.total_devices ?? '—', color: 'text-gray-900', bg: 'bg-gray-50', border: 'border-gray-200' },
          { label: 'Online', value: health?.online_count ?? '—', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
          { label: 'Degraded', value: health?.degraded_count ?? '—', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
          { label: 'Offline', value: health?.offline_count ?? '—', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
          { label: 'Blocked', value: health?.blocked_count ?? '—', color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' },
          { label: 'Unknown', value: health?.unknown_count ?? '—', color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' },
        ].map(({ label, value, color, bg, border }) => (
          <div key={label} className={`rounded-xl border ${border} ${bg} p-4`}>
            <p className="text-[11px] text-gray-500">{label}</p>
            <p className={`mt-1 text-[26px] font-bold leading-tight ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {attentionCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <AlertTriangle size={15} />
          <span><strong>{attentionCount}</strong> device{attentionCount !== 1 ? 's' : ''} need attention (offline or blocked)</span>
        </div>
      )}

      {/* Top offline devices */}
      {(health?.top_offline_devices?.length ?? 0) > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
            <AlertTriangle size={15} className="text-red-400" />
            <h2 className="text-[15px] font-bold text-gray-900">Top Offline Devices</h2>
            <span className="ml-auto text-[12px] text-gray-400">oldest last-seen first</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Device</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Property</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Team</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Health</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Last Seen</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Firmware</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Open Incidents</th>
                </tr>
              </thead>
              <tbody>
                {health!.top_offline_devices.map((d) => (
                  <tr key={d.device_id} className="border-t border-gray-100 hover:bg-gray-50/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Cpu size={13} className="text-gray-400" />
                        <code className="text-[12px] text-gray-700">{d.mac_address}</code>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-gray-700">{d.property_name}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-500">{d.team_name ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${healthColor(d.health_status)}`}>
                        {d.health_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-red-500">{fmtRelative(d.last_seen_at)}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-500">{d.firmware_version ?? '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {d.open_incidents > 0 ? (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">{d.open_incidents}</span>
                      ) : (
                        <span className="text-[11px] text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Org breakdown */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
          <Wifi size={15} className="text-brand-green" />
          <h2 className="text-[15px] font-bold text-gray-900">By Organisation</h2>
          <span className="ml-1 text-[12px] text-gray-400">click row to expand device list</span>
        </div>
        {loading ? (
          <p className="py-12 text-center text-[13px] text-gray-400">Loading...</p>
        ) : !health || health.organizations.length === 0 ? (
          <p className="py-12 text-center text-[13px] text-gray-400">No data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Organisation</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Total</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500 text-green-600">Online</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-amber-600">Degraded</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-red-500">Offline</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Blocked</th>
                  <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-slate-400">Unknown</th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Health</th>
                </tr>
              </thead>
              <tbody>
                {health.organizations.map((org) => (
                  <OrgHealthRow key={org.organization_id} org={org} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
